import type { JSX } from 'react'

import { Button } from '../design-system/button.tsx'
import { Notice } from '../design-system/notice.tsx'

export interface SignInScreenProps {
  onGoogleSignIn: () => void
  busy?: boolean
  error?: string | null
  /**
   * Whether this environment has an Identity Platform tenant to sign in
   * against. False until EI-268 wires one in.
   */
  provisioned?: boolean
}

/**
 * Signed-out sign-in screen. Google SSO is the only authentication path.
 *
 * There is no email/password path, no magic link and no other provider — the
 * same rule super-ui holds. Adding one is a ticket, not a prop.
 *
 * When the environment has no tenant the control is withheld rather than
 * disabled-with-a-tooltip: a button that cannot ever work invites the reader
 * to keep pressing it, and the popup it would open fails with an Identity
 * Platform code that reads as a broken deployment.
 *
 * @param props - Sign-in screen props.
 * @param props.onGoogleSignIn - Starts the Google popup.
 * @param props.busy - True while the popup is in flight.
 * @param props.error - Inline failure; omitted when null.
 * @param props.provisioned - Whether a tenant exists to sign in against.
 * @returns The Google sign-in control, and an error banner when needed.
 */
export function SignInScreen({
  onGoogleSignIn,
  busy = false,
  error = null,
  provisioned = true
}: SignInScreenProps): JSX.Element {
  if (!provisioned) {
    return (
      <Notice tone="warning" data-testid="sign-in-unprovisioned">
        {
          'No Identity Platform tenant is configured in this environment, so nobody can sign in. EI-268 tracks wiring one in.'
        }
      </Notice>
    )
  }

  return (
    <>
      {error ? (
        <Notice tone="danger" data-testid="sign-in-error">
          {error}
        </Notice>
      ) : null}
      <Button
        data-testid="sign-in-google"
        className="w-full"
        aria-busy={busy}
        disabled={busy}
        onClick={onGoogleSignIn}
      >
        {busy ? 'Signing in…' : 'Sign in with Google'}
      </Button>
    </>
  )
}
